import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, post } from './api';
import { useAuth } from './auth';
import type { RequestFeedback } from './action-feedback-store';

type Policy = { requireArea: boolean; version: number };

export function PrintingAreaPolicySettings() {
  const { user } = useAuth();
  const [policy, setPolicy] = useState<Policy | null>(null), [draft, setDraft] = useState(true);
  const [busy, setBusy] = useState(false), [error, setError] = useState(''), [notice, setNotice] = useState('');
  const saving = useRef(false);
  const allowed = !!user?.roles.includes('SUPERADMIN');
  const load = useCallback(async (feedback: RequestFeedback | false = false) => {
    try {
      const value = await api<Policy>('/printing/policy', { feedback });
      setPolicy(value); setDraft(value.requireArea); setError('');
    } catch (e: any) { setError(e.message); }
  }, []);
  useEffect(() => { if (allowed) void load(); }, [allowed, load]);
  if (!allowed) return null;
  const save = async () => {
    if (!policy || saving.current) return;
    saving.current = true; setBusy(true); setError(''); setNotice('');
    try {
      const value = await post<Policy>('/printing/policy', { requireArea: draft, expectedVersion: policy.version });
      setPolicy(value); setDraft(value.requireArea); setNotice('Đã lưu chính sách khu vực in qua Trạm kết nối.');
    } catch (e: any) { setError(e.message); }
    finally { saving.current = false; setBusy(false); }
  };
  const reload = async () => {
    if (saving.current) return;
    saving.current = true; setBusy(true); setNotice('');
    try { await load({ pending: 'Đang tải lại cấu hình in…', success: 'Đã tải lại cấu hình in qua Trạm kết nối.' }); }
    finally { saving.current = false; setBusy(false); }
  };
  return <section className="ph-panel printing-area-settings" aria-labelledby="printing-area-settings-title">
    <h2 id="printing-area-settings-title">Giới hạn khu vực in</h2>
    <p>Chỉ superadmin được thay đổi. Áp dụng toàn hệ thống cho nhãn kho và mã QR máy.</p>
    {policy && <>
      <label className="printing-area-toggle"><input type="checkbox" checked={draft} disabled={busy} onChange={e => { setDraft(e.target.checked); setNotice(''); }} />
        Bắt buộc xác định khu vực trước khi in qua Trạm kết nối</label>
      <p><b>Bật:</b> chỉ in ở khu vực đang được phân công hoặc check-in máy.</p>
      <p><b>Tắt:</b> không cần nhận máy hay phân công khu vực để in; vẫn chỉ sử dụng máy in được cấp quyền.</p>
      <p className="ph-hint">Tùy chọn này không bật lại máy in đang tạm ngừng, không bỏ xác nhận nhãn mẫu. Đổi chính sách sẽ dừng các yêu cầu cũ chưa bắt đầu gửi.</p>
      <div className="print-buttons"><button className="primary" disabled={busy || draft === policy.requireArea} onClick={() => void save()}>{busy ? 'Đang lưu...' : 'Lưu chính sách in qua Trạm kết nối'}</button>
        <button className="secondary" disabled={busy} onClick={() => void reload()}>Tải lại cấu hình in</button><Link to="/printing">Thiết bị in & hàng đợi</Link></div>
    </>}
    {!policy && !error && <p role="status">Đang tải cấu hình in...</p>}
    {error && <p className="error" role="alert">{error}</p>}{notice && <p role="status">{notice}</p>}
  </section>;
}
